// import BillOfSaleLayout from "@layouts/billofsale/BillOfSaleLayout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { CmsLayout } from "@modules/cms/types/CmsLayout";
import { ReactElement, useEffect } from "react";
import SeoInfoProps from "@common/types/SeoInfoProps";
import getCmsPageData from "@modules/cms/api/getCmsPageData";
import SeoInfo from "@common/components/SeoInfo";
import { useRouter } from "next/router";
import NotFound from "@pages/404";
import { GetStaticPropsContext } from "next";
import CmsLayouts from "@modules/cms/components/CmsLayouts";
import { getCanonicalUrl } from "@talladega/helpers";
import useBillOfSaleDefaultInformation, {
  BillOfSaleDefaultInformation,
} from "@modules/bill-of-sale/hooks/useBillOfSaleDefaultInformation";
import useSessionStorage from "@common/hooks/useSessionStorage";
import PublicLayout from "@layouts/public/components/PublicLayout";

type CmsPageProps = {
  layouts: CmsLayout[];
  seoInfo: SeoInfoProps | null;
};

const BillOfSalePage = ({ layouts, seoInfo }: CmsPageProps) => {
  const router = useRouter();
  const [defaultFormInformation] = useBillOfSaleDefaultInformation();
  const [, setBosInfoFromStorage] =
    useSessionStorage<BillOfSaleDefaultInformation>("bosInfo");

  useEffect(() => {
    if (!router.isReady || !defaultFormInformation) return;
    if (Object.keys(router.query).length === 0) return;

    setBosInfoFromStorage(defaultFormInformation);
  }, [
    defaultFormInformation,
    router.isReady,
    router.query,
    setBosInfoFromStorage,
  ]);

  if (!router.isFallback && (!layouts || layouts.length === 0)) {
    return <NotFound />;
  }

  return (
    layouts &&
    layouts.length > 0 && (
      <>
        {seoInfo && <SeoInfo seoInfo={seoInfo} />}
        <CmsLayouts layouts={layouts} />
      </>
    )
  );
};

BillOfSalePage.pageName = "billOfSale";
BillOfSalePage.getLayout = function getLayout(page: ReactElement) {
  return <PublicLayout>{page}</PublicLayout>;
};

export async function getStaticProps(context: GetStaticPropsContext) {
  const kijijiBillOfSale = "billofsale";
  const { seoInfo, layouts } = await getCmsPageData(
    kijijiBillOfSale,
    context.preview,
    context.locale
  );

  if (seoInfo && !seoInfo.canonical) {
    seoInfo.canonical = getCanonicalUrl(kijijiBillOfSale);
  }

  return {
    props: {
      layouts,
      seoInfo,
      ...(await serverSideTranslations(context.locale ?? "en", [
        "common",
        "common-components",
        "billOfSalePage",
      ])),
    },
  };
}

export default BillOfSalePage;
